/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useContext, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
// core components
import OfferPaymentPlanCard from './OfferPaymentPlanCard';
import OfferPaymentPlanParamsContainer from './OfferPaymentPlanParamsContainer';
import OfferPaymentPlanScheduleParamsContainer from './OfferPaymentPlanScheduleParamsContainer';
import OfferPaymentPlanAdvanceSplitCard from './OfferPaymentPlanAdvanceSplitCard';
import {
  loadOfferPaymentPlan,
  loadOfferPaymentPlanSummary,
  loadAdvanceSplit,
  loadDocumentScheduleCellParams,
  importPaymentPlan,
  exportPaymentPlan,
  generateInvoice,
  downloadInvoice,
  cancelInvoice
} from '../actionCreators';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import { withRouter } from 'react-router-dom';
import documentTypeIds from 'constants/documentTypeIds';
import { useTranslation } from 'react-i18next';
import { RightsContext } from 'providers/RightsContext';

export const OfferPaymentPlanContainer = ({ documentId, siteId, showScheduleImportButton, steps, history, location }) => {
  const { t } = useTranslation();
  const { allowSplitDownpayment, allowScheduleImport } = useContext(ApplicationFeaturesContext);
  const { seasonalSettingsRead } = useContext(RightsContext);
  const isPaymentPlanPage = window.location.pathname.includes('paymentPlan');
  const { paymentPlan, isLoaded, paymentPlanSummary, summaryLoaded } = useSelector(
    state => state.currentOffer.paymentPlanData,
    shallowEqual
  );

  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const [cancellingInvoice, setCancellingInvoice] = useState(false);
  const financialProductId = useSelector(state => state.currentOffer.productSelectionData.financialProductId, shallowEqual);
  const exportPaymentPlanWithTemplateOutput = useSelector(state => state.params.items.exportPaymentPlanWithTemplateId, shallowEqual);

  const { advanceSplit, isAdvanceSplitLoaded, saving } = useSelector(state => state.currentOffer.advanceSplitData, shallowEqual);

  const isFinancialProductSelected = () => Boolean(financialProductId && financialProductId !== 0);

  const { currencyCode, documentTypeId, activeStep } = useSelector(state => state.currentOffer.header, shallowEqual);
  const { scheduleCellParams } = useSelector(state => state.currentOffer.paymentPlanData, shallowEqual);

  const advanceData = { advance: advanceSplit.advanceAmount, currencyCode: currencyCode };
  const dispatch = useDispatch();

  const fileInputRef = useRef(null);

  const handleImportScheduleClick = useCallback(() => {
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  }, []);

  const importPaymentPlanInternal = selectedFile => {
    const reader = new FileReader();
    let file = [];

    reader.onload = event => {
      if (!event) file = Object.values(new Uint8Array(reader.content));
      else file = Object.values(new Uint8Array(event.target.result));

      dispatch(importPaymentPlan(documentId, siteId, file));
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const exportPaymentPlanInternal = useCallback(
    fileType => {
      dispatch(exportPaymentPlan(documentId, siteId, t('OfferEdit.FinancialServices.ScheduleTitle'), fileType));
    },
    [documentId, siteId]
  );

  const [cardExpanded, setCardExpanded] = useState(false);

  const onExpand = (_, expanded) => {
    setCardExpanded(!expanded);
  };

  useEffect(() => {
    if (location.state?.fromAction) return;
    if (!steps) return;
    const currentStep = steps.filter(step => step.id === activeStep)[0];
    if (!isFinancialProductSelected())
      history.push(currentStep.route.replace('{id.documentId}', documentId).replace('{id.siteId}', siteId));
  }, []);

  useEffect(() => {
    if (!summaryLoaded) dispatch(loadOfferPaymentPlanSummary(documentId, siteId));
  }, [summaryLoaded]);

  useEffect(() => {
    if (!isLoaded) {
      dispatch(loadOfferPaymentPlan(documentId, siteId));
    }
    if (!isAdvanceSplitLoaded) {
      if (allowSplitDownpayment) {
        dispatch(loadAdvanceSplit(documentId, siteId));
      }
    }
  }, [documentId, siteId, isLoaded, isAdvanceSplitLoaded, cardExpanded]);

  useEffect(() => {
    if (paymentPlan?.scheduleType && paymentPlan.scheduleType.cellParametrizationYN === true) {
      if (scheduleCellParams != null && scheduleCellParams.isLoaded === false) {
        dispatch(loadDocumentScheduleCellParams(documentId, siteId));
      }
    }
  }, [isLoaded]);

  const handleGenerateInvoice = () => {
    dispatch(generateInvoice(documentId, siteId));
  };

  const handleDownloadInvoice = useCallback(async () => {
    try {
      setIsDownloadingInvoice(true);
      await dispatch(downloadInvoice(documentId, siteId));
    } finally {
      setIsDownloadingInvoice(false);
    }
  }, [documentId, siteId]);

  const handleCancelInvoice = useCallback(async () => {
    try {
      setCancellingInvoice(true);
      await dispatch(cancelInvoice(documentId, siteId));
    } finally {
      setCancellingInvoice(false);
    }
  }, [documentId, siteId]);

  return (
    <>
      {
        <OfferPaymentPlanCard
          paymentPlan={paymentPlan}
          isLoaded={isLoaded}
          paymentPlanSummary={paymentPlanSummary}
          currencyCode={currencyCode}
          importPaymentPlan={importPaymentPlanInternal}
          allowScheduleImport={allowScheduleImport}
          showScheduleImportButton={showScheduleImportButton}
          onExpand={onExpand}
          cardExpanded={cardExpanded}
          exportPaymentPlan={exportPaymentPlanInternal}
          fileInputRef={fileInputRef}
          onImportScheduleClick={handleImportScheduleClick}
          exportPaymentPlanWithTemplateOutput={exportPaymentPlanWithTemplateOutput}
          isInvoiceGenerationVisible={paymentPlan.isInvoiceGenerationVisible}
          canGenerateInvoice={paymentPlan.canGenerateInvoice}
          generateInvoice={handleGenerateInvoice}
          canDownloadInvoice={paymentPlan.canDownloadInvoice}
          isDownloadingInvoice={isDownloadingInvoice}
          downloadInvoice={handleDownloadInvoice}
          cancelInvoice={handleCancelInvoice}
          canCancelInvoice={paymentPlan.canCancelInvoice}
          cancellingInvoice={cancellingInvoice}
        />
      }
      {paymentPlan.scheduleType && seasonalSettingsRead && paymentPlan.scheduleType.cellParametrizationYN === true && isPaymentPlanPage && (
        <OfferPaymentPlanParamsContainer documentId={documentId} siteId={siteId} />
      )}

      {isPaymentPlanPage && documentTypeId === documentTypeIds.OfferModification && (
        <OfferPaymentPlanScheduleParamsContainer documentId={documentId} siteId={siteId} />
      )}

      {!paymentPlan.hidden && advanceSplit && isAdvanceSplitLoaded && allowSplitDownpayment && isPaymentPlanPage && (
        <OfferPaymentPlanAdvanceSplitCard advanceSplit={advanceSplit} saving={saving} advanceData={advanceData} />
      )}
    </>
  );
};

OfferPaymentPlanContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  showScheduleImportButton: PropTypes.bool.isRequired,
  steps: PropTypes.array,
  history: PropTypes.object,
  location: PropTypes.object.isRequired
};

export default withRouter(OfferPaymentPlanContainer);
